// BrowseReportsTab — registry browser for the 16 (and counting)
// server-registered reports. Each card links to /reports/run/:reportId
// where the existing ReportRunnerPage takes over.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, FileBarChart2 } from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { EmptyState } from '../../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { useReportsList, type ReportInfo } from '../../useReports';
import styles from './BrowseReportsTab.module.css';

const BRANCH_LABELS: Record<string, string> = {
  shared: 'Shared',
  scrap: 'Collections',
  skip: 'Skip Management',
  weighbridge: 'Weighbridge',
};

function branchLabel(branch: string): string {
  return BRANCH_LABELS[branch] ?? branch;
}

export function BrowseReportsTab() {
  const navigate = useNavigate();
  const list = useReportsList();
  const reports = list.data ?? [];

  // Group reports by branch so the grid can section them. Falls back to a
  // flat layout when the registry is small/empty.
  const grouped = useMemo(() => {
    const map = new Map<string, ReportInfo[]>();
    for (const r of reports) {
      const arr = map.get(r.branch) ?? [];
      arr.push(r);
      map.set(r.branch, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [reports]);

  if (list.isLoading) {
    return (
      <div className={styles.tab}>
        <Spinner />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className={styles.tab}>
        <EmptyState
          icon={FileBarChart2}
          title="No reports registered"
          description="The server has no reports in its registry."
        />
      </div>
    );
  }

  return (
    <div className={styles.tab}>
      {grouped.map(([branch, branchReports]) => (
        <section key={branch} className={styles.section}>
          <h3 className={styles.sectionHeader}>
            {branchLabel(branch)} · {branchReports.length} report
            {branchReports.length !== 1 ? 's' : ''}
          </h3>
          <div className={styles.grid}>
            {branchReports.map((r) => (
              <article key={r.key} className={styles.card}>
                <h4 className={styles.title}>{r.title}</h4>
                {r.description ? (
                  <p className={styles.description}>{r.description}</p>
                ) : (
                  <p className={styles.description}>&nbsp;</p>
                )}
                <div className={styles.actionRow}>
                  <span className={styles.cardKey}>{r.key}</span>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={ChevronRight}
                    onClick={() =>
                      navigate(`/reports/collections/run/${encodeURIComponent(r.key)}`)
                    }
                  >
                    Run Report
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
